import Layout from '@/components/layout';
import { emailTemplateDefault, replaceTemplateVariables } from '@/constants/emailCode';
import { EmailTypeEnum, SETTING_TYPE } from '@/constants/enum';
import { BlockStack, Box, Button, Card, InlineStack, TextField } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import AppModal from '@/components/AppModal/AppModal';
import AppSaveBar from '@/components/AppSaveBar/AppSaveBar';
import SkeletonLayout from '@/components/SkeletonLayout';
import { SkeletonWrapper } from '@/components/SkeletonLayout/styled';
import { config } from '@/config';
import { openErrorToast, openToast } from '@/helpers';
import {
  useGetCrispDataQuery,
  useGetEmailDetailQuery,
  useGetEmailSettingsQuery,
  useSendTestEmailMutation,
  useUpdateEmailDetailMutation,
} from '@/redux/query';
import ace from 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/theme-chrome';
import 'ace-builds/src-noconflict/worker-css';
import 'ace-builds/webpack-resolver';
import AceEditor from 'react-ace';
import { useDispatch } from 'react-redux';
import { handleSaveEmailBackupSettings, handleSaveEmailSettings } from '@/redux/slice/email.slice';
declare global {
  interface Window {
    ace: typeof ace;
  }
}

const EmailCustomerNotification = () => {
  window.ace = ace;
  const { t } = useTranslation('general_settings');
  const navigate = useNavigate();
  const [isPreview, setIsPreview] = useState(true);
  const [emailContent, setEmailContent] = useState({
    original: {
      subject: '',
      body: '',
    },
    current: {
      subject: '',
      body: '',
    },
  });
  const { data: crispData } = useGetCrispDataQuery({ shop: config.shop });
  const { data: emailSettingData } = useGetEmailSettingsQuery();
  const [isShowModal, setIsShowModal] = useState(false);
  const [updateEmailDetail, { isLoading: isUpdatingEmailDetail }] = useUpdateEmailDetailMutation();
  const [email, setEmail] = useState({
    address: '',
    isError: false,
  });

  const location = useLocation();

  const isPickup = useMemo(() => location.pathname.split('/').pop() === SETTING_TYPE.PICKUP, [location.pathname]);
  const dispatch = useDispatch();

  const {
    data: emailDetailData,
    refetch: refetchEmailData,
    isLoading: isLoadingEmailDetailData,
  } = useGetEmailDetailQuery({
    shop: config.shop,
    type: isPickup ? EmailTypeEnum.ReadyForPickup : EmailTypeEnum.OutForDelivery,
  });

  const [sendTestEmail, { isLoading: isLoadingTestEmail }] = useSendTestEmailMutation();

  const translationKey = useMemo(() => (isPickup ? 'storePickup' : 'deliveryAndShipping'), [isPickup]);

  const primaryAction = useMemo(
    () => (
      <InlineStack gap={'200'}>
        <Button onClick={() => setIsShowModal(true)}>
          {t(`sections.emailNotification.${translationKey}.emailPage.testBtn`)}
        </Button>
        <Button variant="primary" onClick={() => setIsPreview(!isPreview)}>
          {isPreview
            ? t(`sections.emailNotification.${translationKey}.emailPage.editCode`)
            : t(`sections.emailNotification.${translationKey}.emailPage.previewBtn`)}
        </Button>
      </InlineStack>
    ),
    [isPreview, t, translationKey],
  );

  const DATE_TIME_LABEL = useMemo(
    () => ({
      PICKUP_DATE: 'Pickup date',
      DELIVERY_DATE: 'Delivery date',
      PICKUP_TIME: 'Pickup time',
      DELIVERY_TIME: 'Delivery time',
    }),
    [],
  );

  const orderSummary = useMemo(
    () => `
    <tr>
        <td class="order-list__image-cell">
            <img src='https://storage.synctrack.io/megamind-dingdoong/assets/product-example.webp' width="60" height="60" class="order-list__product-image" />
        </td>
        <td class="order-list__product-description-cell">
            <span class="order-list__item-title">The Inventory Not Tracked Snowboard × 1</span>
            <br />
            <span class="order-list__item-variant">Part of: Holiday bundle</span>
        </td>
        <td>
            <p class="order-list__item-price">100 ${crispData?.data.currency}</p>
            <p>&nbsp;</p>
        </td>
    </tr>
    `,
    [crispData?.data.currency],
  );

  const orderDiscount = useMemo(
    () => `
    <tr class="subtotal-line">
      <td>
          <p class="subtotal-line__text">
              Order discount
          </p>
      </td>
      <td class="subtotal-line__value">
          -50 ${crispData?.data.currency}
      </td>
  </tr>
  <tr class="subtotal-line">
  <td class="subtotal-line__title" style="font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, &quot;Roboto&quot;, &quot;Oxygen&quot;, &quot;Ubuntu&quot;, &quot;Cantarell&quot;, &quot;Fira Sans&quot;, &quot;Droid Sans&quot;, &quot;Helvetica Neue&quot;, sans-serif; padding: 2px 0;">
    <p class="discount-text">
      <span class="subtotal-line__discount" style="font-size: 16px; margin-left: 5px;">
        <img src="https://cdn.shopify.com/shopifycloud/shopify/assets/themes_support/notifications/discounttag-23d3dd52a101179fb1461daaba6b77388b99b6154de85840a5245b8d3930a68e.png" width="18" height="18" class="discount-tag-icon" style="vertical-align: middle; margin-right: 6px;">
        <span class="subtotal-line__discount-title">
            ORDER5 (-50 ${crispData?.data.currency})
        </span>
      </span>
    </p>
  </td>
</tr>
  `,
    [crispData?.data.currency],
  );

  const orderTax = useMemo(
    () => `
      <tr class="subtotal-line">
        <td class="subtotal-line__text">Taxes</td>
        <td class="subtotal-line__value">
            50 ${crispData?.data.currency}
        </td>
      </tr>
  `,
    [crispData?.data.currency],
  );

  const orderShipping = useMemo(
    () => `
      <tr class="subtotal-line">
          <td class="subtotal-table__cell">
              <p class="subtotal-line__text">
                  Shipping
              </p>
          </td>
          <td class="subtotal-line__value">
              50 ${crispData?.data.currency}
          </td>
      </tr>
      <td class="subtotal-line__title" style="font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, &quot;Roboto&quot;, &quot;Oxygen&quot;, &quot;Ubuntu&quot;, &quot;Cantarell&quot;, &quot;Fira Sans&quot;, &quot;Droid Sans&quot;, &quot;Helvetica Neue&quot;, sans-serif; padding: 2px 0;">
    <p class="discount-text">
      <span class="subtotal-line__discount" style="font-size: 16px; margin-left: 5px;">
        <img src="https://cdn.shopify.com/shopifycloud/shopify/assets/themes_support/notifications/discounttag-23d3dd52a101179fb1461daaba6b77388b99b6154de85840a5245b8d3930a68e.png" width="18" height="18" class="discount-tag-icon" style="vertical-align: middle; margin-right: 6px;">
        <span class="subtotal-line__discount-title">
            FREESHIPPING 
              (-50 ${crispData?.data.currency})
        </span>
      </span>
    </p>
  </td>
  `,
    [crispData?.data.currency],
  );

  const orderDuties = useMemo(
    () => `
      <tr class="subtotal-line">
        <td class="subtotal-table__cell">
          <p class="subtotal-line__text">
            Duties
          </p>
        </td>
        <td class="subtotal-line__value">
          100 ${crispData?.data.currency}
        </td>
      </tr>
    `,
    [crispData?.data.currency],
  );

  const date = new Date();
  const dayName = date.toLocaleDateString('en-GB', { weekday: 'long' });
  const day = date.toLocaleDateString('en-GB', { day: '2-digit' });
  const month = date.toLocaleDateString('en-GB', { month: 'short' });
  const year = date.toLocaleDateString('en-GB', { year: 'numeric' });
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const formatted = `${dayName}, ${day} ${month} ${year}`;

  const deliveryDateTime = useMemo(
    () => `
          <table class="row actions">
              <tr>
                  <td>
                      <div style="display: flex;">
                          <h4 class="datetime_title">${
                            isPickup ? DATE_TIME_LABEL.PICKUP_DATE : DATE_TIME_LABEL.DELIVERY_DATE
                          }</h4><span>: ${formatted}</span>
                      </div>
                  </td>
              </tr>
              <tr>
                <td>
                    <div style="display: flex;">
                        <h4 class="datetime_title">${
                          isPickup ? DATE_TIME_LABEL.PICKUP_TIME : DATE_TIME_LABEL.DELIVERY_TIME
                        }</h4><span>: ${time}</span>
                    </div>
                </td>
              </tr>
              <tr>
                <td><br /></td>
              </tr>
          </table>
  `,
    [
      DATE_TIME_LABEL.DELIVERY_DATE,
      DATE_TIME_LABEL.DELIVERY_TIME,
      DATE_TIME_LABEL.PICKUP_DATE,
      DATE_TIME_LABEL.PICKUP_TIME,
      formatted,
      isPickup,
      time,
    ],
  );

  const dingdoongShopName = useMemo(() => {
    if (emailSettingData?.data?.logo) {
      return `<img src="${emailSettingData.data.logo}" alt="${config.storeName}" width="100" height="100" />`;
    }

    return `<h1 class="shop-name__text">${config.storeName}</h1>`;
  }, [emailSettingData?.data.logo]);

  const variables = useMemo(
    () => ({
      dingdoong_email_title: isPickup ? 'Your order is ready for pickup' : 'Your order is out for delivery',
      dingdoong_shop_name: dingdoongShopName,
      dingdoong_order_name: '#99999',
      dingdoong_location_name: 'DingDoong Location',
      dingdoong_location_address1: '123 Example Street',
      dingdoong_location_address2: '',
      dingdoong_location_city: 'Hanoi',
      dingdoong_location_province: 'Hanoi',
      dingdoong_location_zip: '100000',
      dingdoong_location_address2_detail: 'Apt 4',
      dingdoong_shop_url: `https://${config.shop}`,
      dingdoong_order_summary: orderSummary,
      dingdoong_order_subtotal: `100 ${crispData?.data.currency}`,
      dingdoong_order_discount: orderDiscount,
      dingdoong_order_shipping: orderShipping,
      dingdoong_order_tax: orderTax,
      dingdoong_order_total: `200 ${crispData?.data.currency}`,
      dingdoong_order_saved: `100 ${crispData?.data.currency}`,
      dingdoong_shop_email: emailSettingData?.data.sender || '',
      order_status_url: '',
      dingdoong_delivery_date_time: deliveryDateTime,
      dingdoong_accent_colour: emailSettingData?.data.accentColour || '#1990C6',
      dingdoong_order_duties: orderDuties,
      dingdoong_location_phone: 'Phone number: +84 123 456 789',
      dingdoong_location_country: 'VietNam',
    }),
    [
      crispData?.data.currency,
      deliveryDateTime,
      dingdoongShopName,
      emailSettingData?.data.accentColour,
      emailSettingData?.data.sender,
      isPickup,
      orderDiscount,
      orderDuties,
      orderShipping,
      orderSummary,
      orderTax,
    ],
  );

  const previewContent = useMemo(
    () => replaceTemplateVariables(emailContent.current.body as string, variables),
    [emailContent, variables],
  );

  const handleSendTestEmail = useCallback(() => {
    sendTestEmail({
      shop: config.shop,
      type: isPickup ? EmailTypeEnum.ReadyForPickup : EmailTypeEnum.OutForDelivery,
      emailTo: email.address,
      template: previewContent,
      subject: emailContent.current.subject,
    })
      .unwrap()
      .then(() => {
        setIsShowModal(false);
        openToast(dispatch, 'Send test email successfully');
      })
      .catch(() => {
        openErrorToast(dispatch, 'Send test email failed');
      });
  }, [dispatch, email.address, emailContent, isPickup, previewContent, sendTestEmail]);

  const isChangeData = useMemo(() => {
    if (!emailDetailData?.data || !emailContent.original || !emailContent.current) return false;
    return (
      emailContent.current.body !== emailContent.original.body || emailContent.current.subject !== emailContent.original.subject
    );
  }, [emailContent, emailDetailData]);

  useEffect(() => {
    if (emailDetailData?.data.body || emailDetailData?.data.subject) {
      setEmailContent({
        original: {
          subject: emailDetailData.data.subject || '',
          body: emailDetailData.data.body || '',
        },
        current: {
          subject: emailDetailData.data.subject || '',
          body: emailDetailData.data.body || '',
        },
      });
    }
  }, [emailDetailData?.data.body, emailDetailData?.data.subject]);

  useEffect(() => {
    if (emailSettingData?.data) {
      dispatch(
        handleSaveEmailSettings({
          sender: emailSettingData.data.sender,
        }),
      );
      dispatch(
        handleSaveEmailBackupSettings({
          sender: emailSettingData.data.sender,
        }),
      );
    }
  }, [emailSettingData?.data, dispatch]);

  return (
    <Layout
      title={
        isPickup
          ? t(`sections.emailNotification.${translationKey}.emailPage.title`)
          : t(`sections.emailNotification.${translationKey}.emailPage.title`)
      }
      primaryAction={primaryAction}
      backAction={{
        content: 'Back',
        onAction: () => navigate(-1),
      }}
    >
      <AppSaveBar
        open={isChangeData}
        onSave={() => {
          updateEmailDetail({
            shop: config.shop,
            type: isPickup ? EmailTypeEnum.ReadyForPickup : EmailTypeEnum.OutForDelivery,
            body: emailContent.current.body as string,
            subject: emailContent.current.subject as string,
          })
            .unwrap()
            .then(() => {
              refetchEmailData();
              openToast(dispatch);
            });
        }}
        onDiscard={() =>
          setEmailContent((prev) => ({
            ...prev,
            current: {
              ...prev.original,
            },
          }))
        }
        loading={isUpdatingEmailDetail}
      />
      {isLoadingEmailDetailData ? (
        <SkeletonWrapper>
          <SkeletonLayout />
        </SkeletonWrapper>
      ) : (
        <Card>
          <BlockStack gap={'300'}>
            <TextField
              label={t(`sections.emailNotification.${translationKey}.emailPage.subjectLabel`)}
              value={emailContent.current.subject}
              onChange={(value) =>
                setEmailContent((prev) => ({
                  ...prev,
                  current: {
                    ...prev.current,
                    subject: value,
                  },
                }))
              }
              autoComplete="off"
            />
            {isPreview && <div className="templateEmail" dangerouslySetInnerHTML={{ __html: previewContent as string }} />}
            {!isPreview && (
              <AceEditor
                mode="html"
                theme="chrome"
                width="100%"
                height="600px"
                value={emailContent.current.body}
                placeholder={t('widgetDesign.colour.css.placeHolder')}
                onChange={(newValue) =>
                  setEmailContent((prev) => ({
                    ...prev,
                    current: {
                      ...prev.current,
                      body: newValue,
                    },
                  }))
                }
                showPrintMargin={false}
                showGutter={true}
                highlightActiveLine={false}
                setOptions={{
                  tabSize: 2,
                  readOnly: false,
                  showLineNumbers: true,
                  wrap: true,
                  scrollX: false,
                  useWorker: false,
                }}
                enableBasicAutocompletion={true}
                enableLiveAutocompletion={true}
              />
            )}
            <Box>
              <Button
                onClick={() =>
                  setEmailContent((prev) => ({
                    ...prev,
                    current: {
                      ...prev.current,
                      subject: isPickup
                        ? 'Your order {{ order_number }} is ready for pickup'
                        : 'Your order {{ order_number }} is out for delivery',
                      body: emailTemplateDefault(isPickup ? EmailTypeEnum.ReadyForPickup : EmailTypeEnum.OutForDelivery),
                    },
                  }))
                }
              >
                {t(`sections.emailNotification.${translationKey}.emailPage.default`)}
              </Button>
            </Box>
          </BlockStack>
        </Card>
      )}
      <AppModal
        open={isShowModal}
        onCancel={() => setIsShowModal(false)}
        onSubmit={handleSendTestEmail}
        title={t(`sections.emailNotification.customerNoti.modalTitle`)}
        cancelText={t(`sections.emailNotification.customerNoti.cancel`)}
        submitText={t(`sections.emailNotification.customerNoti.send`)}
        loading={isLoadingTestEmail}
      >
        <TextField
          label={t(`sections.emailNotification.customerNoti.emailLabel`)}
          autoComplete="off"
          value={email.address}
          onChange={(value) => setEmail((prev) => ({ ...prev, address: value }))}
          error={email.isError && t(`sections.emailNotification.customerNoti.error`)}
          placeholder={t(`sections.emailNotification.customerNoti.emailPlaceholder`)}
        />
      </AppModal>
    </Layout>
  );
};

export default EmailCustomerNotification;
